import { Badge } from '@uhg-abyss/web/ui/Badge';
import { Flex } from '@uhg-abyss/web/ui/Flex';
import { IconSymbol } from '@uhg-abyss/web/ui/IconSymbol';
import { useTranslation } from 'react-i18next';

import { useLagoon } from '../../hooks/useLagoon';
import { usePrimaryCare } from '../../hooks/usePrimaryCare';
import { isPcpSuppressionEligible } from '../../utils/pcpSuppression.utils';
import { getShowEditPCP } from '../../utils/primaryCare.utils';
import { getLoggedInMember } from '../../utils/user.utils';
import { Constants } from '../Constants';
import { ConstantsLagoon } from '../ConstantsLagoon';
import { getFeatureFlag } from '../Utils';
import { formatDateWithoutLeadingZeros } from '../Utils/datesUtils/formatDate';
import { CareTeamDropDown } from './CareTeamProviderCard.styled';

export const ProviderCardHeader = ({
  isPcP,
  menuItems,
  isExternalPlanMember,
  isFuturePcpCard = false,
  pcpEffectiveStartDate,
  pcpEffectiveEndDate,
  showEffectiveStartDate = false,
  showEffectiveEndDate = false,
  showChooseOrEditPcpCTA,
}: {
  isPcP: boolean;
  menuItems: {
    title: string;
    onClick: (event: React.MouseEvent<HTMLButtonElement>) => void;
  }[];
  isExternalPlanMember: boolean;
  isFuturePcpCard?: boolean;
  pcpEffectiveStartDate?: string;
  pcpEffectiveEndDate?: string;
  showEffectiveStartDate?: boolean;
  showEffectiveEndDate?: boolean;
  showChooseOrEditPcpCTA?: boolean;
}) => {
  const { t } = useTranslation();
  const { data } = usePrimaryCare();
  const featureFlags = useLagoon(Constants.LAGOON_TABLE.FEATURE_FLAGS)();
  const { primaryCare } = data;
  const loggedInMemberInfo = getLoggedInMember();

  const enableNonGatedPcpSuppression = getFeatureFlag(
    featureFlags,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_NON_GATED_PCP_SUPPRESSION
  );
  const lockInIndicator = primaryCare?.[0]?.lockInIndicator;
  const showEditPCP = getShowEditPCP(
    loggedInMemberInfo.editPcp,
    lockInIndicator
  );

  const hasCurrentPcp = primaryCare?.[0]?.pcpInfo?.hasPCP;
  const shouldHideEditButton = isFuturePcpCard && hasCurrentPcp;

  const isPcpSuppressed = isPcpSuppressionEligible(
    loggedInMemberInfo,
    enableNonGatedPcpSuppression
  );
  const shouldShowPcpBadge = isPcP && !isPcpSuppressed && !isFuturePcpCard;

  return (
    <Flex
      alignItems={isFuturePcpCard ? 'flex-start' : 'center'}
      direction={isFuturePcpCard ? 'column' : 'row'}
      justify={isFuturePcpCard ? 'flex-start' : 'space-between'}
      style={{
        width: '100%',
        margin: isFuturePcpCard ? '8px' : undefined,
      }}
    >
      {shouldShowPcpBadge && (
        <Badge data-testid="pcp-badge" outline variant="success">
          {t('PCP')}
          {showEffectiveEndDate &&
            pcpEffectiveEndDate &&
            ` ${t('• until')} ${formatDateWithoutLeadingZeros(
              pcpEffectiveEndDate,
              true
            )}`}
        </Badge>
      )}
      {isFuturePcpCard && !isPcpSuppressed && (
        <Badge
          css={{ minWidth: 'fit-content' }}
          data-testid="future-pcp-badge"
          outline
          variant="info"
        >
          {t('Future PCP')}
          {showEffectiveStartDate &&
            pcpEffectiveStartDate &&
            ` ${t('• starting')} ${formatDateWithoutLeadingZeros(
              pcpEffectiveStartDate,
              true
            )}`}
        </Badge>
      )}
      {!(isExternalPlanMember && isPcP) &&
        !shouldHideEditButton &&
        (showChooseOrEditPcpCTA !== false || isPcpSuppressed) && (
          <Flex
            data-testid="care-team-dropdown-wrapper"
            justify={isFuturePcpCard ? 'flex-start' : 'flex-end'}
            onClick={(event: React.MouseEvent) => {
              event.stopPropagation();
            }}
            style={{
              flex: isFuturePcpCard ? 'none' : 1,
            }}
          >
            {(showEditPCP || !isPcP || isFuturePcpCard || isPcpSuppressed) && (
              <CareTeamDropDown
                after={<IconSymbol icon="keyboard_arrow_down" />}
                css={
                  isFuturePcpCard
                    ? { padding: '0px', marginLeft: '4px' }
                    : undefined
                }
                data-testid="care-team-dropdown"
                label={
                  (isPcP || isFuturePcpCard) && !isPcpSuppressed
                    ? t('PRIMARY_CARE_PROVIDER.EDIT_PCP')
                    : t('PRIMARY_CARE_PROVIDER.EDIT')
                }
                menuItems={menuItems}
                outline={false}
              />
            )}
          </Flex>
        )}
    </Flex>
  );
};
